// Library Methods
import { useTranslation } from "react-i18next";
import React, { useState, useMemo } from "react";

// Components
import { Divider, Grid, Typography, TextField, Box } from "@mui/material";
import ExamCardButton from "../ui/ExamCardButton";
import ExamCardToggles from "../ui/ExamCardToggles";
import WhichEyeSection from "../ui/WhichEyeSection";
import TitleWithTooltip from "../../../../../components/UI/TitleWithTooltip";
import ExamCardContainer from "../ui/ExamCardContainer";
import ExamCardHeader from "../ui/ExamCardHeader";

// Utilities
import {
  FixationLossMethodOptions,
  FixationPointShapeOptions,
} from "../../../../../utils/examReportsHelper";
import {
  CustomTextFieldSolid,
  CustomTextFieldOutlinedInputProps,
} from "../../../../../styles/muiStylesHelper";
import {
  DEFAULT_DECIBEL_VALUE_FULL_120,
  GridTypes,
  MAX_DECIBEL_VALUE,
  MIN_DECIBEL_VALUE,
  StimulusTypes,
} from "../../../../../enums";

const Full120Card = ({
  label,
  examAlgorithm,
  selectedExamCards,
  updateExamCardProperty,
  onChangeEye,
  onChangeSectionOrder,
  onToggleCard,
}) => {
  // internationalization
  const { t } = useTranslation();

  const defaultFull120 = {
    examFieldType: examAlgorithm,
    examType: "FULL_120",
    decibelValue: DEFAULT_DECIBEL_VALUE_FULL_120,
    eyeCheckboxes: { right: true, left: true },
    visualGrid: GridTypes.G120,
    stimulusSizeType: StimulusTypes.III,
    fixationType: {
      fixationPointShapeType: FixationPointShapeOptions.Sphere,
      fixationLossMethod: FixationLossMethodOptions.EyeTracking,
    },
    sectionOrder: { right: 0, left: 1 },
    optionalToggles: {
      autoPause: true,
      calibration: true,
      encouragement: true,
      requiresTutorial: true,
      unlimitedTime: false,
    },
  };

  const selectedExamCard = selectedExamCards.find(
    (examCard) => examCard.examFieldType === examAlgorithm
  );
  const examCardInfo = selectedExamCard ?? defaultFull120;
  const isActive = Boolean(selectedExamCard);

  const {
    decibelValue: initialDecibelValue,
    eyeCheckboxes,
    sectionOrder,
    optionalToggles,
    fixationType,
    stimulusSizeType,
  } = examCardInfo;

  const [decibelValue, setDecibelValue] = useState(initialDecibelValue);

  const full120StimulusSizes = useMemo(
    () => [
      { type: StimulusTypes.III, label: StimulusTypes.III },
      { type: StimulusTypes.IV, label: StimulusTypes.IV },
      { type: StimulusTypes.V, label: StimulusTypes.V },
    ],
    []
  );

  const textStyle = {
    color: isActive ? "#FFF" : "#5B6D7A",
    fontSize: "14px",
  };

  return (
    <ExamCardContainer isActive={isActive}>
      <ExamCardHeader
        label={label}
        isActive={isActive}
        onChange={(e) => {
          onToggleCard(e, defaultFull120);
        }}
      />
      <Grid item container display="flex" flexDirection="column" gap="8px">
        {/* Full 120 type */}
        <Grid item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("exams_table_type")}
            tooltip={t("more_available_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />
          <ExamCardButton
            isSelected
            updateValue={() => updateExamCardProperty("examType", "FULL_120")}
            label={t("single")}
            isDisabled={!isActive}
          />
        </Grid>

        {/* Stimulus Intensity */}
        <Grid item display="flex" flexDirection="column" xs gap="8px">
          <TitleWithTooltip
            title={t("report_stimulus_intensity")}
            tooltip={t("stimulus_intensity_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid container item display="flex" gap="8px">
            <TextField
              type="number"
              id="stimulus intensity input"
              disabled={!isActive}
              value={decibelValue}
              onChange={(event) => {
                const newDecibalValue = Number(event?.target?.value);
                setDecibelValue(newDecibalValue);
              }}
              onBlur={(event) => {
                const newDecibalValue = Number(event?.target?.value);
                if (newDecibalValue > MAX_DECIBEL_VALUE) {
                  setDecibelValue(MAX_DECIBEL_VALUE);
                  updateExamCardProperty("decibelValue", MAX_DECIBEL_VALUE);
                } else if (newDecibalValue < MIN_DECIBEL_VALUE) {
                  setDecibelValue(MIN_DECIBEL_VALUE);
                  updateExamCardProperty("decibelValue", MIN_DECIBEL_VALUE);
                } else {
                  setDecibelValue(newDecibalValue);
                  updateExamCardProperty("decibelValue", newDecibalValue);
                }
              }}
              onKeyPress={(event) => {
                const isNotNumber =
                  event?.key === "-" ||
                  event?.key === "+" ||
                  event?.key === ".";
                if (isNotNumber) return;

                const newDecibalValue = Number(event?.target?.value);
                setDecibelValue(newDecibalValue);
                updateExamCardProperty("decibelValue", newDecibalValue);
              }}
              fullWidth
              sx={{ ...CustomTextFieldSolid() }}
              InputProps={{
                inputProps: {
                  min: MIN_DECIBEL_VALUE,
                  max: MAX_DECIBEL_VALUE,
                },
                sx: {
                  ...CustomTextFieldOutlinedInputProps,
                },
              }}
            />
          </Grid>
        </Grid>

        {/* Grid size */}
        <Grid item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("word_grid")}
            tooltip={t("grid_size_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />
          <ExamCardButton
            isSelected
            updateValue={() => updateExamCardProperty("visualGrid", "G_120")}
            label="120"
            isDisabled={!isActive}
          />
        </Grid>

        {/* stimulus */}
        <Grid item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("report_stimulus_size")}
            tooltip={t("stimulus_size_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid container item display="flex" gap="8px">
            {full120StimulusSizes.map((full120StimulusSize, index) => (
              <Grid item xs key={index}>
                <ExamCardButton
                  isSelected={full120StimulusSize.type === stimulusSizeType}
                  updateValue={() =>
                    updateExamCardProperty(
                      "stimulusSizeType",
                      full120StimulusSize.type
                    )
                  }
                  label={full120StimulusSize.label}
                  isDisabled={!isActive}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Fixation */}
        <Grid item display="flex" flexDirection="column" xs gap="8px">
          <TitleWithTooltip
            title={t("fixation")}
            tooltip={t("fixation_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid container item display="flex" gap="8px">
            <Grid item xs>
              <ExamCardButton
                isSelected={
                  FixationPointShapeOptions.Sphere ===
                  fixationType.fixationPointShapeType
                }
                updateValue={() =>
                  updateExamCardProperty("fixationType", {
                    ...examCardInfo.fixationType,
                    fixationPointShapeType: FixationPointShapeOptions.Sphere,
                  })
                }
                label={t("exams_modal_sphere")}
                isDisabled={!isActive}
              />
            </Grid>{" "}
            <Grid item xs>
              <ExamCardButton
                isSelected={
                  FixationPointShapeOptions.Diamond ===
                  fixationType.fixationPointShapeType
                }
                updateValue={() =>
                  updateExamCardProperty("fixationType", {
                    ...examCardInfo.fixationType,
                    fixationPointShapeType: FixationPointShapeOptions.Diamond,
                  })
                }
                label={t("diamond")}
                isDisabled={!isActive}
              />
            </Grid>
          </Grid>

          {/* Fixation loss */}
          <Grid item display="flex" flexDirection="column" xs gap="8px">
            <TitleWithTooltip
              title={t("fixation_loss_method")}
              tooltip={t("fixation_loss_method_tooltip")}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid container item display="flex" gap="8px" height="32px">
              <Grid item xs>
                <ExamCardButton
                  isSelected={
                    FixationLossMethodOptions.EyeTracking ===
                    fixationType.fixationLossMethod
                  }
                  updateValue={() =>
                    updateExamCardProperty("fixationType", {
                      ...examCardInfo.fixationType,
                      fixationLossMethod: FixationLossMethodOptions.EyeTracking,
                    })
                  }
                  label={t("eye_tracking")}
                  isDisabled={!isActive}
                />
              </Grid>
              <Grid item xs>
                <ExamCardButton
                  isSelected={
                    FixationLossMethodOptions.BlindSpot ===
                    fixationType.fixationLossMethod
                  }
                  updateValue={() =>
                    updateExamCardProperty("fixationType", {
                      ...examCardInfo.fixationType,
                      fixationLossMethod: FixationLossMethodOptions.BlindSpot,
                    })
                  }
                  label={t("word_blind_spot")}
                  isDisabled={!isActive}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <WhichEyeSection
          isActive={isActive}
          isRightFirst={sectionOrder.right === 0}
          eyeCheckboxes={eyeCheckboxes}
          onChangeSectionOrder={onChangeSectionOrder}
          onChangeEye={onChangeEye}
        />

        <Box height={{ xs: "0px", sm: "61px" }} />

        <Divider color="#5B6D7A" />

        <Grid display="flex" flexDirection="column">
          <ExamCardToggles
            isActive={isActive}
            optionalToggles={optionalToggles}
            updateExamCardProperty={updateExamCardProperty}
            showToggles={defaultFull120.optionalToggles}
          />
        </Grid>

        <Grid item textAlign="center">
          <Typography color={isActive ? "#FFF" : "#5B6D7A"} fontSize="14px">
            {t("new_test")}
          </Typography>
          <Typography color={isActive ? "#FFF" : "#5B6D7A"} fontSize="14px">
            {t("try_it_out")}
          </Typography>
        </Grid>
      </Grid>
    </ExamCardContainer>
  );
};

export default Full120Card;
